// Shared marketing chrome — brand mark, public top nav, and footer. Used by
// the public landing page, the contact page, and (lightly) the login page so
// the whole signed-out surface shares one look. Dark, on-brand
// (Bug = white, punch = orange #f97316), Geist typeface.

import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink } from '@/lib/icons';

export const MARKETING_FONT = "'Geist Variable', system-ui, sans-serif";
const ORANGE = '#f97316';
const GITHUB_URL = 'https://github.com/oddgames/bugpunch-sdk';

export function BugpunchLogo({ size = 32 }: { size?: number }) {
  return <img src="/logo-512.png" alt="Bugpunch" width={size} height={size} className="block rounded-md" />;
}

export function BugpunchWordmark({ className = '' }: { className?: string }) {
  return (
    <span className={className}>
      <span className="text-white">Bug</span>
      <span style={{ color: ORANGE }}>punch</span>
    </span>
  );
}

export function BrandLockup({ size = 30, text = 'text-[19px]' }: { size?: number; text?: string }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 no-underline shrink-0">
      <BugpunchLogo size={size} />
      <BugpunchWordmark className={`${text} font-semibold tracking-tight`} />
    </Link>
  );
}

/** Sticky translucent top bar for every signed-out page. */
export function MarketingHeader({ ctaHref = '/contact', ctaLabel = 'Request access' }: { ctaHref?: string; ctaLabel?: string }) {
  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#0a0b0d]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        <BrandLockup />
        <nav className="hidden items-center gap-7 md:flex">
          <a href="/#features" className="text-sm text-white/55 transition-colors hover:text-white">Features</a>
          <a href="/#how" className="text-sm text-white/55 transition-colors hover:text-white">How it works</a>
          <a href="/#platforms" className="text-sm text-white/55 transition-colors hover:text-white">Platforms</a>
          <Link to="/docs" className="text-sm text-white/55 transition-colors hover:text-white">Docs</Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/login" className="hidden rounded-lg px-3 py-2 text-sm text-white/70 transition-colors hover:text-white sm:inline-block">
            Sign in
          </Link>
          <a
            href={ctaHref}
            className="group inline-flex items-center gap-1 rounded-lg bg-[#f97316] px-4 py-2 text-sm font-semibold text-white shadow-[0_4px_20px_-4px_rgba(249,115,22,0.6)] transition-colors hover:bg-[#ea6a0c]"
          >
            {ctaLabel}
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </header>
  );
}

export function MarketingFooter() {
  const year = 2026;
  return (
    <footer className="border-t border-white/[0.06] bg-[#0a0b0d]">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-xs">
            <BrandLockup size={26} text="text-[17px]" />
            <p className="mt-3 text-sm leading-relaxed text-white/40">
              Crash reporting, live remote debugging, and automated QA for Unity games — on every device.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-14 gap-y-2 text-sm sm:grid-cols-3">
            <FooterCol title="Product">
              <a href="/#features" className="footer-link text-white/50 hover:text-white">Features</a>
              <a href="/#platforms" className="footer-link text-white/50 hover:text-white">Platforms</a>
              <Link to="/docs" className="text-white/50 hover:text-white">Documentation</Link>
            </FooterCol>
            <FooterCol title="Developers">
              <Link to="/docs/getting-started" className="text-white/50 hover:text-white">Getting started</Link>
              <Link to="/docs/api-reference" className="text-white/50 hover:text-white">API reference</Link>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-white/50 hover:text-white">
                GitHub <ExternalLink className="h-3 w-3" />
              </a>
            </FooterCol>
            <FooterCol title="Company">
              <Link to="/contact" className="text-white/50 hover:text-white">Contact</Link>
              <Link to="/login" className="text-white/50 hover:text-white">Sign in</Link>
              <a href="mailto:enquiry@bugpunch.com" className="text-white/50 hover:text-white">enquiry@bugpunch.com</a>
            </FooterCol>
          </div>
        </div>
        <div className="mt-10 flex flex-col gap-2 border-t border-white/[0.05] pt-6 text-xs text-white/30 sm:flex-row sm:items-center sm:justify-between">
          <span>© {year} ODD Games. All rights reserved.</span>
          <span>Built for Unity 6000.0+ · Android · iOS · Standalone</span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="mb-1 text-xs font-semibold uppercase tracking-wider text-white/30">{title}</span>
      {children}
    </div>
  );
}
